import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, IconButton, Avatar,
  Menu, MenuItem, Paper, TextField, Autocomplete, Button, Divider,
} from '@mui/material';
import { LocalHospital, ArrowBack, ExitToApp, Print } from '@mui/icons-material';
import { patientService, userService, printSettingsService, authService, issuedCertificateService } from '../../services/api';
import { generateCertNo } from '../../utils/certNumber';

const fmt = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const todayStr = () => new Date().toISOString().split('T')[0];

const MedicalLeaveCertificateForm3 = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = useState(null);

  const [patients, setPatients]               = useState([]);
  const [doctors, setDoctors]                 = useState([]);
  const [printSettings, setPrintSettings]     = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDoctor, setSelectedDoctor]   = useState(null);

  const [form, setForm] = useState(() => ({
    servantName: '',
    condition:   '',
    days:        '',
    effectFrom:  todayStr(),
    date:        todayStr(),
    certNo:      generateCertNo('MLCF3'),
  }));

  const printRef = useRef();

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, dRes, sRes] = await Promise.all([
          patientService.getAllPatients(),
          userService.getDoctors(),
          printSettingsService.getSettings(),
        ]);
        setPatients(pRes.data);
        setDoctors(dRes.data);
        setPrintSettings(sRes.data);
        if (user?.role === 'Doctor') {
          const match = dRes.data.find((d) => d.id === user.id);
          if (match) setSelectedDoctor(match);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      }
    };
    load();
  }, []);

  const handlePatientChange = (val) => {
    setSelectedPatient(val);
    setForm((prev) => ({
      ...prev,
      servantName: val ? `${val.firstName} ${val.lastName}` : '',
    }));
  };

  const handleField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const isPrePrinted = (printSettings?.letterheadType || printSettings?.LetterheadType || 'Pre-Printed Letterhead') === 'Pre-Printed Letterhead';
  const mt = printSettings?.marginTop    ?? printSettings?.MarginTop    ?? 10;
  const mb = printSettings?.marginBottom ?? printSettings?.MarginBottom ?? 10;
  const ml = printSettings?.marginLeft   ?? printSettings?.MarginLeft   ?? 10;
  const mr = printSettings?.marginRight  ?? printSettings?.MarginRight  ?? 10;
  const hospital        = printSettings?.hospitalName || printSettings?.HospitalName || '';
  const hospitalAddress = printSettings?.address      || printSettings?.Address      || '';
  const hospitalPhone   = printSettings?.phone        || printSettings?.Phone        || '';

  const handlePrint = async () => {
    try {
      await issuedCertificateService.save({
        certificateNo:       form.certNo,
        certificateType:     'MLCF3',
        certificateTypeName: 'Medical Certificate for Leave (Form 3)',
        patientId:           selectedPatient?.id || null,
        patientName:         selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : '',
        doctorId:            selectedDoctor?.id || null,
        doctorName:          selectedDoctor ? `Dr. ${selectedDoctor.firstName} ${selectedDoctor.lastName}` : '',
        certDate:            form.date || new Date().toISOString().split('T')[0],
        details:             JSON.stringify({ servantName: form.servantName, condition: form.condition, days: form.days, effectFrom: form.effectFrom }),
      });
    } catch (err) { console.error('Failed to save certificate record:', err); }

    const printContents = printRef.current.innerHTML;
    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>Medical Certificate for Leave (Form 3)</title>
          <style>
            body { font-family: Arial, sans-serif; margin: ${mt}mm ${mr}mm ${mb}mm ${ml}mm; font-size: 13px; color: #000; line-height: 2.2; }
            .f3-title { text-align: center; font-weight: bold; text-decoration: underline; letter-spacing: 0.5px; }
            .f3-right { text-align: right; margin-top: 32px; font-weight: bold; }
          </style>
        </head>
        <body>${printContents}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const doctorFullName = selectedDoctor
    ? `Dr. ${selectedDoctor.firstName} ${selectedDoctor.lastName}`
    : '';
  const regNo = selectedDoctor?.medicalRegistrationNumber || '';

  // underline style helpers
  const ul   = { borderBottom: '1px solid #000', display: 'inline-block', minWidth: 220, textAlign: 'center' };
  const ulSm = { ...ul, minWidth: 120 };
  const ulMd = { ...ul, minWidth: 170 };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/reports')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Medical Certificate for Leave (Form 3)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>
            <IconButton size="large" onClick={(e) => setAnchorEl(e.currentTarget)} color="inherit">
              <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { authService.logout(); navigate('/'); }}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', gap: 2, p: 3, alignItems: 'flex-start' }}>

        {/* Left Panel */}
        <Paper elevation={3} sx={{ width: '30%', flexShrink: 0, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>Certificate Details</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            <Autocomplete
              options={patients}
              getOptionLabel={(p) => `${p.firstName} ${p.lastName}`}
              value={selectedPatient}
              onChange={(_, val) => handlePatientChange(val)}
              renderInput={(params) => <TextField {...params} label="Select Patient (Govt. Servant)" size="small" required />}
            />

            <TextField
              label="Name of Govt. Servant (Shri)"
              size="small"
              value={form.servantName}
              onChange={handleField('servantName')}
            />

            <Autocomplete
              options={doctors}
              getOptionLabel={(d) => `Dr. ${d.firstName} ${d.lastName}`}
              value={selectedDoctor}
              onChange={(_, val) => setSelectedDoctor(val)}
              renderInput={(params) => <TextField {...params} label="Authorised Medical Attendant (I)" size="small" required />}
            />

            <TextField
              label="Suffering From (Condition)"
              size="small"
              multiline
              rows={2}
              value={form.condition}
              onChange={handleField('condition')}
            />

            <TextField
              label="Period of Absence (days)"
              size="small"
              value={form.days}
              onChange={handleField('days')}
            />

            <TextField
              label="With Effect From"
              type="date"
              size="small"
              value={form.effectFrom}
              onChange={handleField('effectFrom')}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Date"
              type="date"
              size="small"
              value={form.date}
              onChange={handleField('date')}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Certificate No."
              size="small"
              value={form.certNo}
              onChange={handleField('certNo')}
            />

            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{ mt: 1, background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)' }}
            >
              Print Certificate
            </Button>
          </Box>
        </Paper>

        {/* Right Panel — Preview */}
        <Paper elevation={3} sx={{ flex: 1, p: 4, borderRadius: 2, minHeight: 600 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>Preview</Typography>

          <Box
            ref={printRef}
            sx={{ fontFamily: 'Arial, sans-serif', fontSize: '0.9rem', color: '#000', lineHeight: 2.2 }}
          >
            {/* Hospital header — hidden for Pre-Printed Letterhead */}
            {!isPrePrinted && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                  [{hospital}{hospitalAddress ? `, ${hospitalAddress}` : ''}{hospitalPhone ? `, ${hospitalPhone}` : ''}]
                </Typography>
              </Box>
            )}

            {/* Title block */}
            <Typography className="f3-title" sx={{ fontWeight: 800, fontSize: '1.05rem', textAlign: 'center', textDecoration: 'underline', letterSpacing: 0.5 }}>
              FORM – 3
            </Typography>
            <Typography className="f3-title" sx={{ fontWeight: 800, fontSize: '1.0rem', textAlign: 'center', textDecoration: 'underline', letterSpacing: 0.5, mb: 3 }}>
              MEDICAL CERTIFICATE FOR LEAVE
            </Typography>

            <Divider sx={{ mb: 3, borderColor: '#000' }} />

            {/* Signature line */}
            <Typography sx={{ mb: 2 }}>
              Signature of the Government Servant&nbsp;&nbsp;
              <span style={{ ...ul, minWidth: 280 }}>{form.servantName}</span>
            </Typography>

            {/* I (doctor) line */}
            <Typography sx={{ mb: 0 }}>
              I&nbsp;
              <span style={{ ...ul, minWidth: 280 }}>{doctorFullName}</span>
              {' '}after careful personal examination of the case
            </Typography>

            {/* Certify line */}
            <Typography sx={{ mb: 0 }}>
              here by certify that Shri{' '}
              <span style={ulMd}>{form.servantName}</span>
              {' '}whose signature is given
            </Typography>

            {/* Suffering from */}
            <Typography sx={{ mb: 0 }}>
              above, is suffering from{' '}
              <span style={{ ...ul, minWidth: 200 }}>{form.condition}</span>
              {' '}and I&nbsp; consider that a period
            </Typography>

            {/* Absence period */}
            <Typography sx={{ mb: 3 }}>
              of absence from duty of{' '}
              <span style={ulSm}>{form.days}</span>
              {' '}with effect from{' '}
              <span style={ulMd}>{form.effectFrom ? fmt(form.effectFrom) : ''}</span>
              {' '}is
              {' '}absolutely necessary for the restoration of the health.
            </Typography>

            {/* Date */}
            <Typography sx={{ mb: 1 }}>
              <strong>Date.:</strong>
              <span style={{ ...ulSm, minWidth: 160, marginLeft: 8 }}>{form.date ? fmt(form.date) : ''}</span>
            </Typography>

            {/* Certificate No */}
            <Typography sx={{ mb: 4 }}>
              <strong>Certificate No.:</strong>
              <span style={{ ...ulSm, minWidth: 160, marginLeft: 8 }}>{form.certNo}</span>
            </Typography>

            {/* Right-aligned signature */}
            <div className="f3-right" style={{ textAlign: 'right', marginTop: 32 }}>
              <Typography className="f3-right" sx={{ fontWeight: 700, fontSize: '0.95rem', textAlign: 'right' }}>
                Authorised Medical Attendant
              </Typography>
              {regNo && (
                <Typography sx={{ fontSize: '0.88rem', mt: 0.5, textAlign: 'right' }}>
                  Regd. No.: <span style={{ ...ulSm, minWidth: 100 }}>{regNo}</span>
                </Typography>
              )}
            </div>
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default MedicalLeaveCertificateForm3;
